/**
 * @format
 */
import { RangeOffset, SelectedElement, ShortcutTypes, TemplateVariableElement } from "../types/editor-types";
import { Injectable } from "@angular/core";

import { BsModalAlertComponent, createAlertModalOptions } from "../../core/modal/modal-alert.component";
import { BsModalService } from "ngx-bootstrap/modal";
import { LinkModalComponent } from "../components/link/link-modal.component";
import { CustomKeyboardEvent } from "../../core/content-editable/types";
import { BaseToolService } from "./base-tool.service";

@Injectable({ providedIn: "root" })
export class EditorService extends BaseToolService {
   private _isSelectAll: boolean;
   private _selectedElement: SelectedElement;
   private _windowSelection: Selection;
   private _range: Range;
   private _regExpToSearchTemplateVariable = new RegExp("template.", "g");
   private _templateIdAndHtmlElement: TemplateVariableElement;
   private _templateContextElement: Element;
   private _isParameter: boolean;

   constructor(private modalService: BsModalService) {
      super();
      this.initialize();
   }

   initialize(): void {
      this._windowSelection = null;
      this._range = null;
      this._isSelectAll = false;
      this._selectedElement = null;
      this._templateIdAndHtmlElement = null;
      this._isParameter = false;
      this._templateContextElement = null;
   }

   getSelectedContentHtml(selection: Selection, isParameter: boolean, isSelectAll: boolean, id: any): any {
      let html = isParameter ? this._templateContextElement.parentElement.outerHTML : "";

      if (!isParameter && typeof selection != "undefined") {
         let sel = selection;
         if (sel.rangeCount) {
            const container = document.createElement("div");
            if (!isSelectAll) {
               for (let i = 0, len = sel.rangeCount; i < len; ++i) {
                  container.appendChild(sel.getRangeAt(i).cloneContents());
               }
            } else {
               container.innerHTML = document.getElementById(id).innerHTML;
            }
            html = container.innerHTML;
         }
      }
      return html;
   }

   splitHtmlIntoRowsByDiv(htmlSelected: string, isParameter: boolean): string[] {
      if (htmlSelected) {
         const htmlRows = htmlSelected
            .split(/<div>/)
            .map((rowHtml) => (isParameter ? rowHtml : rowHtml.replace(/<\/div>/g, "")))
            .map((rowHtml) => rowHtml.replace(/<br>/g, ""));
         //.filter((rowHtml) => rowHtml.trim() !== "<br>");
         return htmlRows;
      }
      return [];
   }

   getTemplateIdAndHtmlElement(selection: Selection): TemplateVariableElement | null {
      try {
         let elementRef: HTMLElement;
         const range = selection.getRangeAt(0);

         if (this.isText(range.startContainer)) {
            const currentNode = this.getElementWithNgModelAttribute(range.startContainer);
            elementRef = <HTMLElement>currentNode;
         } else {
            const currentNode = this.getElementWithNgModelAttribute(range.endContainer);
            elementRef = <HTMLElement>currentNode;
         }

         if (!Boolean(elementRef)) {
            this.openAlertDialog("Alert", "Wrong selection, please select only text!");
            return null;
         }

         const templateId = elementRef.getAttribute("ng-model");
         return {
            id: templateId.replace(this._regExpToSearchTemplateVariable, ""),
            htmlElement: elementRef,
         };
      } catch (e) {
         console.error("Getting Template Id and HTMLElement", e);
         return null;
      }
   }

   private getElementWithNgModelAttribute(node: Node): Node | null {
      if (!Boolean(node)) {
         return null;
      }

      if (this.hasNgModelAttribute(node)) {
         return node;
      }

      return this.getElementWithNgModelAttribute(node.parentNode);
   }

   isWrongSelection(event: PointerEvent) {
      const firstNode = <Node>event.composedPath()[0];
      return !this.hasAttributeAncestorNode(firstNode, ["ng-model", "contenteditable"]);
   }

   hasAttributeAncestorNode(node: Node, attributes: Array<string>): boolean {
      try {
         if (!Boolean(node) || attributes.length === 0 || this.hasIdEqualTo(node, "editor__body")) {
            return false;
         }

         if (this.hasAttributes(node, attributes)) {
            return true;
         }

         return this.hasAttributeAncestorNode(node.parentNode, attributes);
      } catch (error) {
         this.logError(error, "hasAttributeAncestorSelection");
         return false;
      }
   }

   hasAttributes(node: Node, attributes: Array<string>): boolean {
      if (!Boolean(node) || attributes.length === 0) {
         return false;
      }

      return attributes.reduce((prevResult, attribute) => {
         return prevResult && this.hasAttribute(node, attribute);
      }, true);
   }

   hasAnyTextSelection(): boolean {
      try {
         const selection = window.getSelection();
         const range = selection.getRangeAt(0);
         return this.isText(range.startContainer) || this.isText(range.endContainer);
      } catch (error) {
         this.logError(error, "Is Wrong Selection");
         return true;
      }
   }

   isUploadImageButtonClicked(event: PointerEvent): boolean {
      try {
         const path = event.composedPath();
         return this.isInput(<Node>path[0]) && (<Element>path[0]).id === "input-file";
      } catch (error) {
         this.logError(error, "Current Target Outside Content");
         return false;
      }
   }

   escapedTextForRegexp(text: string): string {
      //  text = text.replace(/(<span class="label2 label-info">)([^])*?(<\/span>)(<\/span>)/g, "");
      text = text.replace(/\$/g, "dollar").replace(/\/n/g, "<br>").trim();
      return text;
   }

   isParameter(event: PointerEvent): boolean {
      try {
         const path = event.composedPath();

         const hasParameter = (element: Element, currentAttempt: number, attemptsMax: number) =>
            currentAttempt < attemptsMax && this.isTemplateContext(element);

         const foundElement = path.find((element: Element, index) => hasParameter(element, index, 5));

         return Boolean(foundElement);
      } catch (error) {
         this.logError(error, "isParameter");
         return false;
      }
   }

   getParentSelectedElement(selection): any {
      let parentSelectedElement = selection.anchorNode.parentElement;
      if (selection.isParameter) {
         return selection.parameterParentElement;
      }

      if (
         Boolean(parentSelectedElement) &&
         Boolean(parentSelectedElement.attributes["contenteditable"]) &&
         parentSelectedElement.attributes["contenteditable"] !== true &&
         parentSelectedElement.innerHTML !== selection.innerHtmlSelected
      ) {
         return parentSelectedElement.parentElement;
      }

      return parentSelectedElement;
   }

   setRangeFromSelection(selection: Selection): void {
      this.setRange(selection.rangeCount === 0 ? null : selection.getRangeAt(0));
   }

   resetRangeSelection(): void {
      this.setRange(undefined);
   }

   insertInRangeSelection(selectedInnerHTML: string): void {
      try {
         const newNode = this.getWrapperElementFromInnerHMTL(selectedInnerHTML).firstElementChild;
         const currentRange = this.getRange();
         currentRange.deleteContents();
         currentRange.insertNode(newNode);
      } catch (error) {
         this.logError(error, "Insert in Range Selection");
      }
   }

   restoreRangeSelection(): void {
      if (!Boolean(this.getRange())) {
         return;
      }
      const selection = window.getSelection();
      selection.removeAllRanges();
      const currentRange = this.getRange();

      if (currentRange) {
         selection.addRange(currentRange);
         this.setWindowSelection(selection);
      }
   }

   hasClickedOnCurrentSelection(event: PointerEvent): boolean {
      const { templateVariable } = this.getSelectedElement() || {};

      if (!Boolean(templateVariable)) {
         return;
      }

      const path = event.composedPath();

      const hasTemplateVariableId = (element: HTMLElement, currentAttempt: number, attemptsMax: number) =>
         currentAttempt < attemptsMax &&
         typeof element === "object" &&
         (Boolean(element.attributes["ng-model"]) || element.id === `template.${templateVariable}`);

      const foundElement = path.find((element: HTMLElement, index) => hasTemplateVariableId(element, index, 5));

      return Boolean(foundElement);
   }

   hasClickedOnInputColorPicker(event: PointerEvent): boolean {
      const { templateVariable } = this.getSelectedElement() || {};

      if (!Boolean(templateVariable)) {
         return false;
      }
      const path = event.composedPath();
      const firstNode = path[0];
      return this.isInput(<Node>firstNode);
   }

   openAlertDialog(title: string, message: string): void {
      this.modalService.show(BsModalAlertComponent, createAlertModalOptions(title, message));
   }

   openAddLinkDialog(): void {
      this.modalService.show(LinkModalComponent, {
         backdrop: "static",
         class: "modal-md",
         keyboard: false, // disable esc key
      });
   }

   getOffset(range: Range): RangeOffset {
      return { start: range.startOffset, end: range.endOffset };
   }

   logError(error: Error, method: string): void {
      console.error(`(custom error) catch in ${method} `, error);
   }

   getWrapperElementFromInnerHMTL(innerHTML: string): Element {
      const spanWrapper = document.createElement("SPAN");
      spanWrapper.innerHTML = innerHTML;
      return spanWrapper;
   }

   isShortcutPress(type: ShortcutTypes, event: CustomKeyboardEvent<Element>): boolean {
      if (!Boolean(event.metaKey) && !Boolean(event.ctrlKey)) {
         return false;
      }

      if (type === "UNDERLINE") {
         return event.code === "KeyU";
      }

      if (type === "BOLD") {
         return event.code === "KeyB";
      }

      if (type === "SELECT_ALL") {
         return event.code === "KeyA";
      }

      if (type === "UNDO") {
         return event.code === "KeyZ";
      }
      return false;
   }

   isArrowKeyPress(event: CustomKeyboardEvent<Element>): boolean {
      return (
         event.code === "ArrowUp" ||
         event.code === "ArrowDown" ||
         event.code === "ArrowLeft" ||
         event.code === "ArrowRight"
      );
   }

   createSelectedElement(templateVariableElement?: TemplateVariableElement): Partial<SelectedElement> {
      try {
         if (Boolean(templateVariableElement)) {
            this._templateIdAndHtmlElement = templateVariableElement;
         }

         const { id, htmlElement } = this._templateIdAndHtmlElement;
         const currentWindowSelection = window.getSelection();

         this.setWindowSelection(currentWindowSelection);
         this.setRangeFromSelection(currentWindowSelection);

         if (this._isParameter) {
            this.preventParameterSelection();
         }

         const htmlSelected = this.getSelectedContentHtml(
            currentWindowSelection,
            this._isParameter,
            this.getIsSelectedAll(),
            `template.${id}`
         );

         // when we select more than one row at a time
         const htmlSelectedRows = this.splitHtmlIntoRowsByDiv(htmlSelected, this._isParameter);

         this.setSelectedElement({
            templateVariable: id,
            originalValues: htmlSelectedRows,
            containerRef: htmlElement,
            innerHtmlSelected: htmlSelected,
            anchorNode: currentWindowSelection.anchorNode,
            parameterParentElement: this._isParameter ? this._templateContextElement : undefined,
            isParameter: this._isParameter,
         });

         /**
          * When text it's being selected the all the options displayed in all dropdown
          * should display and match as current selection
          */
         //this.editorService.updateDropDownFromSelection(this.fontDropDown, this._selection, this.fontSource);
         this.setIsSelectAll(false);
         // console.log("Current selection >>", this._selection);
         return this.getSelectedElement();
      } catch (e) {
         this.logError(e, "createSelectedElement");
      }
   }

   setNgModelValueAsIdToElement(rootElement: Element): void {
      const ngModelValue = rootElement.getAttribute("ng-model");
      if (rootElement.id === "") {
         rootElement.id = ngModelValue;
      }
   }

   preventParameterSelection(): void {
      const currentRange = this.getRange();
      if (currentRange.startContainer !== currentRange.endContainer) {
         return;
      }

      const extractedElement = this.getTemplateContextNode(currentRange.startContainer);

      if (Boolean(extractedElement)) {
         this._templateContextElement = extractedElement;
         this.setSelectionForParameter(this._templateContextElement);
      }
   }

   getTemplateContextNode(node: Node): Element | null {
      if (!Boolean(node) || !Boolean(node.parentNode)) {
         return null;
      }

      if (this.isTemplateContext(node.parentNode)) {
         return node.parentElement;
      }
      return this.getTemplateContextNode(node.parentNode);
   }

   setIsSelectAll(value: boolean): void {
      this._isSelectAll = value;
   }

   getIsSelectedAll(): boolean {
      return this._isSelectAll;
   }

   setWindowSelection(value: Selection): void {
      this._windowSelection = value;
   }

   correctSelectionIfAnyIsNotText(currentSelection: Selection): Selection {
      try {
         let newStart: Node = null;
         let newEnd: Node = null;
         const newRange = new Range();
         const range = currentSelection.getRangeAt(0);
         const { startContainer, endContainer, startOffset, endOffset } = range;
         if (this.isText(startContainer) && this.isText(endContainer)) {
            return currentSelection;
         }

         if (
            startContainer === endContainer &&
            startOffset === endOffset &&
            this.isTemplateContextSibling(startContainer, startOffset)
         ) {
            const nbspNode = document.createTextNode("\u00A0");
            const startNode = this.getChildrenFromNodeByPosition(startContainer, startOffset);
            this.insertNodeAfterChild(<Node & ParentNode>startContainer, startNode, nbspNode);
            newStart = nbspNode;
            newEnd = newStart;
         }

         if (
            startContainer === endContainer &&
            startOffset === endOffset &&
            !this.isTemplateContextSibling(startContainer, startOffset)
         ) {
            const nbspNode = document.createTextNode("\u00A0");
            const startNode = this.getChildrenFromNodeByPosition(startContainer, startOffset);
            this.insertNodeBeforeChild(<Node & ParentNode>startContainer, startNode, nbspNode);
            newStart = nbspNode;
            newEnd = newStart;
         }

         if (!this.isText(range.startContainer) && !Boolean(newStart)) {
            const rangeStartNode = this.getChildrenFromNodeByPosition(startContainer, startOffset);
            newStart = this.getFirstTextNode(rangeStartNode);
         } else if (!Boolean(newStart)) {
            newStart = startContainer;
         }

         if (!this.isText(endContainer) && !Boolean(newEnd)) {
            const rangeEndNode = this.getChildrenFromNodeByPosition(endContainer, endOffset);
            newEnd = this.getLastTextNode(rangeEndNode);
         } else if (!Boolean(newEnd)) {
            newEnd = endContainer;
         }

         if (Boolean(newStart) && !Boolean(newEnd)) {
            newEnd = newStart;
         }

         if (!Boolean(newStart) && Boolean(newEnd)) {
            newStart = newEnd;
         }

         if (!Boolean(newStart) || !Boolean(newEnd)) {
            throw Error("Check newStart or newEnd node values because some is null");
         }

         newRange.setStart(newStart, 0);
         newRange.setEnd(newEnd, newEnd.nodeValue.length);
         currentSelection.removeAllRanges();
         currentSelection.addRange(newRange);

         return currentSelection;
      } catch (error) {
         this.logError(error, "Correcting Selection on first click event");
         return currentSelection;
      }
   }

   getWindowSelection(): Selection {
      return this._windowSelection;
   }

   setRange(range: Range): void {
      this._range = range;
   }

   getRange(): Range {
      return this._range;
   }

   getSelectedElement(): SelectedElement | null {
      return this._selectedElement;
   }

   setSelectedElement(selectedEl: SelectedElement): void {
      this._selectedElement = selectedEl;
   }

   getTemplateContextElement(): Element {
      return this._templateContextElement;
   }

   setTemplateContextElement(templateContext: Element): void {
      this._templateContextElement = templateContext;
   }

   setIsParameter(value: boolean): void {
      this._isParameter = value;
   }

   getIsParameter(): boolean {
      return this._isParameter;
   }

   getNewSelectionRange(startId: string, endId: string): void {
      try {
         const startNode = document.getElementById(startId);
         const endNode = document.getElementById(endId);

         if (!Boolean(startNode)) {
            return;
         }
         const newRange = new Range();

         if (!Boolean(endNode) && this.isStyledNodeWitEmptyText(startNode)) {
            const nbspNode = document.createTextNode("\u00A0");
            startNode.appendChild(nbspNode);

            newRange.setStart(nbspNode, 0);
            newRange.setEnd(nbspNode, nbspNode.length);

            startNode.removeAttribute("id");
         } else if (!Boolean(endNode)) {
            const { length: endOffset } = this.getFirstTextOrParameterNodeIfExist(startNode)?.nodeValue ?? "";

            newRange.setStart(this.getFirstTextOrParameterNodeIfExist(startNode), 0);
            newRange.setEnd(this.getFirstTextOrParameterNodeIfExist(startNode), endOffset);

            startNode.removeAttribute("id");
         } else {
            const { length: endOffset } = this.getLastTextOrParameterNodeIfExist(endNode)?.nodeValue ?? "";

            newRange.setStart(this.getFirstTextOrParameterNodeIfExist(startNode), 0);
            newRange.setEnd(this.getLastTextOrParameterNodeIfExist(endNode), endOffset);

            startNode.removeAttribute("id");
            endNode.removeAttribute("id");
         }
         const selection = window.getSelection();

         selection.removeAllRanges();
         selection.addRange(newRange);
         this.setWindowSelection(selection);
         this.setRange(newRange);
      } catch (error) {
         // then we need to add logic to handle get the correct range
         this.logError(error, "getNewSelectionRange");
      }
   }

   getNewSelectionRangeForParameter(startId: string, endId: string): void {
      try {
         const startNode = document.getElementById(startId);
         const endNode = document.getElementById(endId);

         this.setSelectionForParameter(this.getTemplateContextElement());

         if (Boolean(startNode)) {
            startNode.removeAttribute("id");
         }
         if (Boolean(endNode)) {
            startNode.removeAttribute("id");
         }
      } catch (error) {
         // then we need to add logic to handle get the correct range
         this.logError(error, "getNewSelectionRangeForParameter");
      }
   }

   setSelectionForParameter(parameterElement: Node): void {
      const newRange = new Range();
      const firstTextNode = this.getFirstTextNode(parameterElement);
      const lastTextNode = this.getLastTextNode(parameterElement);
      newRange.setStart(firstTextNode, 0);
      newRange.setEnd(lastTextNode, lastTextNode.nodeValue.length);

      this.setRange(newRange);

      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(newRange);

      this.setWindowSelection(selection);
   }

   isNodeInEditorBody(node: Node): boolean {
      const editorBodyRefElement = Array.from(document.getElementsByClassName("editor__body"))[0];

      return editorBodyRefElement.contains(node);
   }

   isRootEditableNode(node: Node): boolean {
      return this.hasAttributes(node, ["ng-model", "contenteditable"]);
   }

   getInnerHtmlContentRectified(content: string): string {
      const auxiliaryElement = document.createElement("div");
      auxiliaryElement.id = "temporaryBody";
      auxiliaryElement.style.visibility = "hidden";
      auxiliaryElement.innerHTML = content;
      document.body.appendChild(auxiliaryElement);
      const editableElements = Array.from(
         document.querySelectorAll('div[id="temporaryBody"] div[ng-model*="template"]')
      );

      editableElements.forEach((element) => {
         const childNodes = Array.from(element.childNodes);
         const innerHtml = childNodes.reduce((previous, child, index) => {
            return previous + this.getChildRectify(child);
         }, "");
         element.innerHTML = innerHtml;
      });

      const newInnerHtml = auxiliaryElement.innerHTML;
      auxiliaryElement.remove();
      return newInnerHtml;
   }

   private getChildRectify(node: Node): string {
      if (!this.hasSiblingNode(node)) {
         if (this.isText(node)) {
            return `<p style="margin: 0;">${node.nodeValue}</p>`;
         }

         if (!this.isText(node) && !this.isDiv(node) && !this.isP(node)) {
            return `<p style="margin: 0;">${(<Element>node).outerHTML}</p>`;
         }
      }

      if (!Boolean(node.previousSibling) || this.isDiv(node.previousSibling) || this.isP(node.previousSibling)) {
         if (this.isText(node)) {
            return `<p style="margin: 0;">${node.nodeValue}`;
         }

         if (!this.isText(node) && !this.isDiv(node) && !this.isP(node)) {
            return `<p style="margin: 0;">${(<Element>node).outerHTML}`;
         }
      }

      if (!Boolean(node.nextSibling) && !this.isDiv(node) && !this.isP(node)) {
         if (this.isText(node)) {
            return `${node.nodeValue}</p>`;
         } else {
            return `${(<Element>node).outerHTML}</p>`;
         }
      }

      if (this.isDiv(node)) {
         const element = <Element>node;
         const innerHtmlChild = this.removeParagraphDivAndReplaceStrong(element.innerHTML);

         if (Boolean(node.previousSibling) && !this.isDiv(node.previousSibling) && !this.isP(node.previousSibling)) {
            return `</p><p style="margin: 0;">${innerHtmlChild}</p>`;
         }

         return `<p style="margin: 0;">${innerHtmlChild}</p>`;
      }

      if (this.isP(node)) {
         const element = <Element>node;
         const innerHtmlChild = element.innerHTML;
         element.innerHTML = this.removeParagraphDivAndReplaceStrong(innerHtmlChild);

         if (Boolean(node.previousSibling) && !this.isDiv(node.previousSibling) && !this.isP(node.previousSibling)) {
            return `</p>${element.outerHTML}`;
         }

         return element.outerHTML;
      }

      if (this.isText(node)) {
         return node.nodeValue;
      }

      return (<Element>node).outerHTML;
   }

   private removeParagraphDivAndReplaceStrong(innerHtml: string): string {
      innerHtml = innerHtml.replace(/<div>/g, "");
      innerHtml = innerHtml.replace(/<\/div>/g, "");
      innerHtml = innerHtml.replace(/<p>/g, "");
      innerHtml = innerHtml.replace(/<\/p>/g, "");
      innerHtml = innerHtml.replace(/<strong>/g, "<b>");
      innerHtml = innerHtml.replace(/<\/strong>/g, "</b>");
      return innerHtml;
   }

   private getChildrenFromNodeByPosition(node: Node, offset: number): Node {
      if (node.childNodes.length === offset) {
         return Array.from(node.childNodes)[offset - 1];
      }
      return Array.from(node.childNodes)[offset];
   }

   private insertNodeAfterChild(parentNode: Node & ParentNode, childNode: Node, newChild: Node) {
      if (parentNode.childNodes.length === 0) {
         this.appendAllChildren(parentNode, [newChild]);
      } else {
         const brotherNodes = this.getAllNextSiblingNodesFromNode(childNode);
         this.removeAllChildFromParent(parentNode, [childNode, ...brotherNodes]);
         this.appendAllChildren(parentNode, [childNode, newChild, ...brotherNodes]);
      }
   }

   private insertNodeBeforeChild(parentNode: Node & ParentNode, childNode: Node, newChild: Node) {
      if (parentNode.childNodes.length === 0) {
         this.appendAllChildren(parentNode, [newChild]);
      } else {
         const brotherNodes = this.getAllNextSiblingNodesFromNode(childNode);
         this.removeAllChildFromParent(parentNode, [childNode, ...brotherNodes]);
         this.appendAllChildren(parentNode, [newChild, childNode, ...brotherNodes]);
      }
   }

   private isTemplateContextSibling(rangeContainer: Node, rangeOffset: number): boolean {
      const children = Array.from(rangeContainer.childNodes);
      // console.group("Is TemplateContext Sibling");
      // console.log({ OFFSET: rangeOffset });
      // console.table(children, ["nodeName"]);
      // console.groupEnd();
      return children.length === rangeOffset && this.isTemplateContext(children[rangeOffset - 1]);
   }
}
